"use client";
import { TPartner, TPartnerData } from "@/app/types/partnerPageTypes";
import Banner from "../shared/Banner";
import BannerPlaceholder from "../shared/BannerPlaceholder";
import PartnersSection from "./PartnersSection";
import { chunkData } from "@/app/utils/helperFunctions";
import PartnerBG from "./PartnerBG";
import { useContext, useEffect, useMemo, useRef } from "react";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import gsap from "gsap";

const PartnersComponents = ({
	banner,
	partners,
	initialBannerImageSrc,
}: TPartnerData & { initialBannerImageSrc?: string }) => {
	const { isIpad } = useContext(screenSizeContext);
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);

	const { imgSrc, videoSrc } = useMediaGetter(banner?.media);
	const bannerImageSrc = imgSrc ?? initialBannerImageSrc;

	const partnerChunks = useMemo(() => {
		if (!partners?.length) return [] as TPartner[][];

		const partnersData = partners.map((item: any) => ({
			imgSrc:
				process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL +
				item.logo?.media_name,
			description: item?.description?.en,
			title: item?.title?.en,
		}));

		return chunkData(partnersData, 3) as TPartner[][];
	}, [partners]);

	useEffect(() => {
		if (bannerImageSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [bannerImageSrc, setIsPageFetched]);

	useEffect(() => {
		if (partnerChunks.length > 0) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [partnerChunks.length, setIsPageFetched]);

	const tl = useRef<GSAPTimeline>(gsap.timeline());
  useEffect(() => {
    if (isPageFetched === "fetched" && bannerImageSrc) {
      const timer = setTimeout(()=> {
        const html = document.documentElement;

        tl.current
        .timeScale(html.classList.contains("loaded") ? 1.25 : 1)
        .set(".banner .hero-title-word", { yPercent: 50, alpha: 0 })
        .set(".banner .hero-video", { clipPath: "inset(50% 50% 50% 50%)", scale: 0.75, transformOrigin: "center center" })
        .to(".banner .hero-image img", {
          delay: html.classList.contains("loaded") ? 1.1 : 1.6,
          duration: 1,
          ease: "power2.inOut",
          y: "0%"
        })
        .to(".banner .hero-image-wrapper", {
          duration: 1,
          ease: "power2.inOut",
          scale: 1,
        }, "-=0.25")
        .to(".navbar_section", {
          duration: 1,
          ease: "power2.inOut",
          y: "0%"
        }, "-=0.4")
        .to(".banner .hero-video", {
          duration: 1,
          ease: "expo.inOut",
          scale: 1,
          clipPath: "inset(0% 0% 0% 0%)",
        }, "<")
        .to(".banner .hero-title-word", {
          duration: 1,
          stagger: 0.05,
          ease: "expo.inOut",
          yPercent: 0,
          alpha: 1
        }, "<");
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isPageFetched, bannerImageSrc]);

	return (
		<div className={`page_wrapper ${isIpad && "partners-page"}`}>
			{bannerImageSrc ? (
				<Banner
					priority
					imageSrc={`${bannerImageSrc}`}
					title={banner?.title?.en}
					description={banner?.description?.en}
					videoSrc={videoSrc || "/owest-banner.mp4"}
					minHeight={"86vh"}
					optimizedImage
				/>
			) : (
				<BannerPlaceholder minHeight="86vh" />
			)}
			<div className="content_wrapper || relative my-11 lg:mt-[6.25rem] lg:pb-[6.25rem] bg-white min-h-[20rem]">
				{partnerChunks.map((item: TPartner[], index: number) => (
					<PartnersSection
						data={item}
						key={index}
						last={index === partnerChunks.length - 1}
						optimizedImage
					/>
				))}
				<PartnerBG />
			</div>
		</div>
	);
};

export default PartnersComponents;
